import { palette } from '@/theme/constants';
import { Box, Grid, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { authSelector, memorySelector, storySelector } from '@/store/selectors';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { MuiIconButton } from '@/components';
import { FormikConfig } from './formik';
import {
  setShowMediaButton,
  sendMemory,
  setMediaType,
  updateMemory,
  openModal,
  openSubscriptionPopup,
  updateSubscriptionStatus,
  refreshUserData,
  closeModal,
  setMediaScreenType,
  selectEditMemory,
  updateMemoryViewG,
} from '@/store/actions';
import {
  FetchFileService,
  cdn_url,
  checkRoleAndPermission,
  complementaryDownload,
  complementaryUpload,
  fileConverter,
} from '@/utils';

import TitleStep from '../../../TitleStep';
import MediaUploadStep from '../../../MediaUploadStep';
import { useRouter } from 'next/router';
import { RefreshUserData } from '@/utils/fetchUserData';
import { UseFirstRender } from '@/hooks';

export const Form: FC<any> = ({
  submitForm,
  SetMainStep,
  formRef,
  changeMediaTypeScreen,
  setHasChanges,
  prompt,
  isLoading,
  divRef,
  setIsLoading,
  setExistData,
}) => {
  const { t } = useTranslation();
  const { mediaScreenType, memoriesLoaded, showMediaButtons, memoryData } = useSelector(memorySelector);

  const [fileName, setFileName] = useState<any>('');
  const [type, setType] = useState<any>('');
  const [openModalLoading, setOpenModalLoading] = useState(false);
  const { story } = useSelector(storySelector);
  const [ClearInput, SetClearInput] = useState(false);
  const [defaultItem, setDefaultItem] = useState<any>(null);
  const { user } = useSelector(authSelector);
  const [isEditing, SetisEditing] = useState(false);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const router = useRouter();

  const saveComplementarys = async (data: any) => {
    const { complementaryMedia, complementaryText, mediaFiles, richText } = data;

    const newData: any = {};
    newData.memory_details = {};

    let totalSize = 0;
    const allMedia = [...(complementaryMedia || []), ...(mediaFiles || [])];

    const mappedMedia = allMedia.map((item: any) => {
      if (item?.url) {
        return {
          file: item,
          isFeatured: item.isFeatured || false,
        };
      }

      return {
        file: item.file || item,
        isFeatured: item.isFeatured || false,
      };
    });

    const complementaryImage = mappedMedia.filter((item: any) => {
      const type = item.file?.type || '';
      const url = item.file?.url || '';

      return (
        type.includes('image') ||
        url.endsWith('.webp') ||
        url.endsWith('.jpg') ||
        url.endsWith('.jpeg') ||
        url.endsWith('.png') ||
        url.endsWith('.heic') ||
        url.endsWith('.heif')
      );
    });

    const complementaryVideo = mappedMedia.filter((item: any) => {
      const type = item.file?.type || '';
      const url = item.file?.url || '';

      return (
        type.includes('video') ||
        url.endsWith('.mp4') ||
        url.endsWith('.mov') || // iPhone QuickTime format
        type === 'video/quicktime'
      );
    });

    const complementaryAudio = mappedMedia.filter((item: any) => {
      const type = item.file?.type || '';
      const url = item.file?.url || '';

      return (
        type.includes('audio') ||
        url.endsWith('.mp3') ||
        url.endsWith('.aac') ||
        url.endsWith('.wav') ||
        url.endsWith('.m4a') ||
        type === 'audio/x-m4a' ||
        type === 'audio/m4a'
      );
    });

    try {
      if (complementaryImage.length > 0) {
        complementaryImage.forEach((item: any) => {
          if (item?.file?.size) totalSize += item.file.size;
        });
        newData.memory_details.complementaryImage = await complementaryUpload(
          dispatch,
          complementaryImage,
          story,
          false,
          true,
        );
      }
    } catch (err) {
      dispatch(openModal({ content: 'Image upload failed. Please try again.' }));
      throw new Error('Image upload failed');
    }

    try {
      if (complementaryVideo.length > 0) {
        complementaryVideo.forEach((item: any) => {
          if (item?.file?.size) totalSize += item.file.size;
        });
        newData.memory_details.complementaryVideo = await complementaryUpload(
          dispatch,
          complementaryVideo,
          story,
          true,
        );
      }
    } catch (err) {
      dispatch(openModal({ content: 'Video upload failed. Please try again.' }));
      throw new Error('Video upload failed');
    }

    try {
      if (complementaryAudio.length > 0) {
        complementaryAudio.forEach((item: any) => {
          if (item?.file?.size) totalSize += item.file.size;
        });
        newData.memory_details.complementaryAudio = await complementaryUpload(dispatch, complementaryAudio, story);
      }
    } catch (err) {
      dispatch(openModal({ content: 'Audio upload failed. Please try again.' }));
      throw new Error('Audio upload failed');
    }

    if (complementaryText?.length > 0) {
      newData.memory_details.complementaryText = complementaryText;
    }

    if (richText?.length > 0) {
      newData.memory_details.complementaryText = newData.memory_details.complementaryText ? richText : richText;
    }

    let type = 'text';
    const hasFeaturedImage = complementaryImage.some((item: any) => item.isFeatured);
    if (hasFeaturedImage) {
      type = 'image';
    } else if (complementaryVideo.length > 0) {
      type = 'video';
    } else if (complementaryAudio.length > 0) {
      type = 'audio';
    }

    return { ...newData, totalSize, type };
  };

  const saveMemoryData = async (data: any) => {
    const newData = { ...data };
    try {
      const complements = await saveComplementarys(data);

      if (defaultItem) {
        newData.id = defaultItem?.id;
        dispatch(updateMemory({ ...newData, ...complements, router, storyId: story?.url }));
      } else {
        dispatch(sendMemory({ ...newData, ...complements, router, storyId: story?.url }));
      }
    } catch (error) {
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      dispatch(refreshUserData());
      dispatch(updateSubscriptionStatus({ userId: story?.user?.id }));
      await RefreshUserData(user?.token, story?.user?.id);

      data.story_id = story?.id;
      const isOwner = story?.user_id === user?.id;

      if (
        !checkRoleAndPermission(story?.user?.roles, 'Subscriber_Individual', 'CLIENT_MEMORY_CREATE', story?.user_id)
      ) {
        handleSubscriptionError(isOwner);
        return;
      }
      await saveMemoryData(data);
    } catch (error) {
      dispatch(openModal({ content: 'An unexpected error occurred. Please try again.' }));
      setIsLoading(false);
    }
  };

  const handleSubscriptionError = (isOwner: boolean) => {
    const message = isOwner ? 'You are not a subscriber.' : 'The creator is not a subscriber.';
    dispatch(openModal({ content: message }));

    if (isOwner) {
      setTimeout(() => dispatch(openSubscriptionPopup()), 1000);
    }

    dispatch(closeModal());
    setIsLoading(false);
  };

  const generateFilePath = (fileName: string, title: string, type: string, timestamp: number) => {
    const sanitizedFileName = fileName?.split('/')?.pop();

    if (type === 'video') return `videos/${timestamp}_${sanitizedFileName}`;

    return `stories/${title}/memory/${timestamp}_${sanitizedFileName?.replace(/\.[^.]+$/, '.webp')}`;
  };

  const getFileType = (type: string, mediaType: string) => (type === 'image' ? 'image/webp' : mediaType);

  const uploadFile = async (uploadUrl: string, media: any, type: string) => {
    setIsLoading(true);
    const fileToUpload = type === 'image' ? await fileConverter(media) : media;
    const response = await FetchFileService(uploadUrl, 'PUT', fileToUpload, getFileType(type, media?.type));

    if (!response?.ok) throw new Error('Upload failed');
  };

  const finalizeSubmission = () => {
    changeMediaTypeScreen();
    dispatch(setHasChanges(false));
    setIsLoading(false);
    setOpenModalLoading(false);
  };

  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });

  const {
    values,
    handleSubmit: formikSubmit,
    handleChange: formikHandleChange,
    errors,
    touched,
    setTouched,
    setFieldValue,
    resetForm,
  } = FormikConfig(handleSubmit);

  const ref = useRef<HTMLDivElement | null>(null);

  const setDefaults = async () => {
    SetisEditing(true);
    try {
      let mediaFiles: any = [];
      let complementaryMedia: any = [];
      const keys = Object.keys(defaultItem || {});
      const valuesKeys = Object.keys(values || {});
      let mainAssetUrl: string | null = null;

      if (!defaultItem) {
        setIsLoading(false);
        return;
      }

      for (const key of keys) {
        if (valuesKeys.includes(key)) setFieldValue(key, defaultItem[key]);

        if (key === 'memory_details') {
          const memoryDetails = defaultItem[key];
          if (!memoryDetails) continue;

          const detailsKeys = Object.keys(memoryDetails);

          for (const detailKey of detailsKeys) {
            if (detailKey !== 'complementaryText') {
              try {
                const value: any = await complementaryDownload(dispatch, memoryDetails[detailKey]);
                if (value) complementaryMedia = [...complementaryMedia, ...value];
              } catch (error) {
                console.error(`Error fetching complementary media (${detailKey}):`, error);
              }
            }

            if (valuesKeys.includes(detailKey)) {
              if (detailKey == 'complementaryText') {
                setFieldValue('richText', memoryDetails[detailKey]);
              } else {
                setFieldValue(detailKey, memoryDetails[detailKey]);
              }
              setFieldValue(detailKey, memoryDetails[detailKey]);
            }
          }
        }

        if (key === 'asset' && memoryData?.mediaType !== 'text') {
          mainAssetUrl = `${cdn_url}${defaultItem[key]}`;
          try {
            const fetchImage: any = await complementaryDownload(dispatch, defaultItem[key]);
            if (fetchImage) mediaFiles = [...mediaFiles, ...fetchImage];
          } catch (error) {
            console.error('Error fetching asset:', error);
          }
          dispatch(setMediaType('media'));
        }

        if (key === 'asset' && memoryData?.mediaType === 'text' && defaultItem?.type === 'text') {
          dispatch(setMediaType('text'));
          setFieldValue('richText', JSON.parse(defaultItem[key]));
        }
      }

      const isMainAssetFeatured = complementaryMedia.some((img: any) => img.isFeatured && img.url === mainAssetUrl);

      if (isMainAssetFeatured) {
        mediaFiles = [];
      }

      setFieldValue('mediaFiles', [...complementaryMedia, ...mediaFiles]);

      dispatch(setShowMediaButton());
      setIsLoading(false);
      SetisEditing(false);
    } catch (error) {
      console.error('Error in setDefaults:', error);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);
    setDefaults();
  }, [defaultItem, memoryData?.mediaType]);

  const handleChange = (e: any) => {
    if (e?.target) {
      if (values[e.target.name] !== e.target.value) {
        dispatch(setHasChanges(true));
      }
      formikHandleChange(e);
    } else {
      const { name, value } = e;
      if (values[name] !== value) {
        dispatch(setHasChanges(true));
      }
      setFieldValue(name, value);
    }
  };

  const selectTypeOfScreen = () => {
    const memoryEdit = memoriesLoaded?.find((item: any) => item.id == router?.query?.memoryId);

    if (memoryEdit) {
      setDefaultItem(memoryEdit);
      dispatch(setMediaScreenType('form'));
      dispatch(setMediaType(memoryEdit?.type));

      dispatch(selectEditMemory(null));
      dispatch(updateMemoryViewG(memoryEdit?.user_id));
      return;
    }
  };

  UseFirstRender(() => {
    selectTypeOfScreen();
  }, [router?.query?.memoryId, memoriesLoaded?.length, story?.story_details?.prompts?.length]);




  return (
    <>
      {/* {isLoading && isEditing ? (
        <Box width={'100%'} height={'100vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Image src='/images/Loading.svg' quality={'100'} alt='loading' fill style={{ objectFit: 'cover' }} />
        </Box>
      ) : ( */}
      <>
        <Box
          px={'1rem'}
          sx={{
            width: '100%',
            height: isMobile ? 80 : 160,
            position:isMobile ? 'relative' : 'relative',
            top:0,
            zIndex:10,
            overflow: 'hidden',
            // backgroundColor:  'rgba(0, 0, 0, 1)',
            backdropFilter: 'blur(20px)',
          }}>
          <Box
            sx={{
              width: '100%',

              maxWidth: '1200px',
              margin: '0 auto',
              position: 'relative',
              top: isMobile ? 30 : 114,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <IconButton
              onClick={() => { router.push(`/app/story/${story?.url}`)}}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: 'white',
              }}>
              <Image src='/icons/left-arrow.svg' width={20} height={20} alt='close' />
            </IconButton>

{!defaultItem?.id ? (
  <Typography color={palette?.white} align="center"  fontSize="26px">
    Create Memory
  </Typography>
) : (
  <Typography color={palette?.white} align="center" mt={1} fontSize="1rem">
    {values?.title}
  </Typography>
)}
          </Box>

          <>
            {/* { coverLoading &&  ( */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                backgroundSize: '200% 100%',
                animation: 'loadingShimmer 3s infinite',
              }}
            />
            {!isMobile && story?.cover_image && (
              <Image
                priority
                src={`${cdn_url}${story.cover_image}`}
                fill={true}
                alt='hello'
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,

                  transition: 'opacity 0.2s ease-in-out',
                }}
              />
            )}
            <>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '1200px',
                  margin: '0 auto',
                  position: 'relative',
                  top: 150,
                  zIndex: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}></Box>
            </>

            {/* Blur Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backdropFilter: 'blur(60px)',
                zIndex: 3,
              }}
            />
          </>
        </Box>

        <form
          ref={formRef}
          onSubmit={formikSubmit}
          style={{ width: '100%', maxWidth: '600px', margin: '0 auto', paddingTop: isMobile ? '16px' : '5rem' }}>
          <Grid container spacing={2}>

              <MediaUploadStep
                errors={errors}
                touched={touched}
                handleOnTouched={handleOnTouched}
                handleChange={handleChange}
                values={values}
                setFieldValue={setFieldValue}
              />
          </Grid>
        </form>
          <Box display='flex' justifyContent='center' my={2}>
            <MuiIconButton
              icon='/icons/tick'
              label={defaultItem?.id ? 'Update Memory' : ' Save Memory'}
              loading={isLoading}
              altIcon='Memvy'
              iconPosition='left'
              direction='row'
              background='rgba(33, 45, 109, 1)'
              width={!isMobile ? '353px' : '310px'}
              isRounded={false}
              height={!isMobile ? '58px' : '58px'}
              iconHeight={12.8}
              iconWidth={8.8}
              sx={{
                marginTop: '10px',
                borderRadius: '10px',
                gap: '1rem',
              }}
              method={submitForm}
            />
          </Box>
      </>
    </>
  );
};
